'use client';
import { useState, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { logout } from '@/utils/auth';
import { switchToAccount } from '@/utils/switchAccount';
import { useToast } from '@/hooks/useToast';
import { useUserProfile } from '@/hooks/useUserProfile';
import { usePermissions, buildCanHelpers } from '@/hooks/usePermissions';
import { useSuperAdminPermissions } from '@/hooks/useSuperAdminPermissions';
import SafeImage from '@/components/ui/SafeImage';
import { Menu, Bug, FileText } from 'lucide-react';

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CircularProfileSelector = ({ users, currentUser, onSwitch }) => {
  const [rotation, setRotation] = useState(0);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const startAngle = useRef(0);
  const lastIndex = useRef(-1);

  // Radius of the circle
  const RADIUS = 140; // Slightly larger arc
  // Angle separation between items
  const SEPARATION_ANGLE = 50; // More separation for larger icons

  // Initial centering of the active user
  useEffect(() => {
    if (users && users.length > 0) {
      const activeIndex = users.findIndex(u => u.id === currentUser?.id || u.id === currentUser?._id);
      if (activeIndex !== -1) {
        // -ActiveIndex * SEP puts active item at 0 rotation relative (which is -90 absolute)
        const targetRot = -activeIndex * SEPARATION_ANGLE;
        setRotation(targetRot);
        lastIndex.current = activeIndex;
      }
    }
  }, [users, currentUser]);

  const getAngle = (clientX, clientY) => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height; // Pivot at bottom center
    const x = clientX - centerX;
    const y = clientY - centerY;
    // Calculate angle in degrees, 0 is 3 o'clock, -90 is 12 o'clock
    return Math.atan2(y, x) * (180 / Math.PI);
  };

  const handleStart = (clientX, clientY) => {
    isDragging.current = true;
    startAngle.current = getAngle(clientX, clientY) - rotation;
  };

  const handleMove = (clientX, clientY) => {
    if (!isDragging.current) return;
    const angle = getAngle(clientX, clientY);
    const newRotation = angle - startAngle.current;

    setRotation(newRotation);
  };

  const handleEnd = () => {
    isDragging.current = false;

    // Snap to nearest item
    // Index 0 is at -90deg (absolute) when rotation is 0.
    // Index i is at -90 + (i * SEP) + ROT.
    // To center index i at -90, we need ROT = -(i * SEP).
    // So current index = -ROT / SEP.

    let index = Math.round(-rotation / SEPARATION_ANGLE);

    // Clamp index to valid range
    const clampedIndex = Math.max(0, Math.min(users.length - 1, index));
    const snapRotation = -clampedIndex * SEPARATION_ANGLE;
    setRotation(snapRotation);

    // Auto-select if changed
    if (clampedIndex !== lastIndex.current) {
      lastIndex.current = clampedIndex;
      const targetUser = users[clampedIndex];
      // Only switch if it's not the already active user
      if (targetUser && (targetUser.id !== currentUser?.id && targetUser.id !== currentUser?._id)) {
        onSwitch(targetUser);
      }
    }
  };

  // Touch handlers
  const onTouchStart = (e) => handleStart(e.touches[0].clientX, e.touches[0].clientY);
  const onTouchMove = (e) => handleMove(e.touches[0].clientX, e.touches[0].clientY);
  const onTouchEnd = handleEnd;

  // Mouse handlers
  const onMouseDown = (e) => handleStart(e.clientX, e.clientY);
  const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
  const onMouseUp = handleEnd;
  const onMouseLeave = () => { if (isDragging.current) handleEnd(); };

  return (
    <div className="relative w-full h-[210px] overflow-hidden -mt-2 mb-4 select-none touch-none"
      ref={containerRef}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseLeave}
    >
      {/* Dashed guide circle */}
      <div className="absolute bottom-0 left-1/2 w-[340px] h-[340px] -translate-x-1/2 rounded-full border border-dashed border-gray-300 pointer-events-none opacity-40"></div>

      {/* Items */}
      {users.map((u, i) => {
        // Position logic
        const itemAngle = -90 + (i * SEPARATION_ANGLE) + rotation;
        const rad = itemAngle * (Math.PI / 180);

        const x = Math.cos(rad) * RADIUS;
        const y = Math.sin(rad) * RADIUS;

        const isActive = u.id === currentUser?.id || u.id === currentUser?._id;

        // Visibility Check
        // Items far from -90 deg fade out
        // Distance from zenith (-90)
        let dist = Math.abs(itemAngle + 90);
        const opacity = Math.max(0, 1 - (dist / 100)); // Fade out over 100 degrees

        // Scale effect for active/center item
        const scale = isActive ? 1.3 : 0.9;

        // Don't render if invisible to save performance
        if (opacity <= 0.05) return null;

        return (
          <div
            key={u.id}
            className="absolute left-1/2 bottom-0 w-20 h-20 -ml-10 -mb-10 flex flex-col items-center justify-center transition-transform duration-300 ease-out cursor-pointer"
            style={{
              transform: `translate(${x}px, ${y}px) scale(${scale})`,
              opacity: opacity,
              zIndex: Math.round(opacity * 100)
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Click to select/snap
              const newRot = -i * SEPARATION_ANGLE;
              setRotation(newRot);
              if (!isActive) onSwitch(u);
            }}
          >
            {u.photoUrl ? (
              <SafeImage src={u.photoUrl} alt={u.name} variant="avatar" name={u.name} className={`w-16 h-16 rounded-full border-4 object-cover pointer-events-none ${isActive ? 'border-primary-500 shadow-xl' : 'border-white shadow-md'}`} />
            ) : (
              <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center text-2xl font-bold shadow-md pointer-events-none ${u.role === 'student' ? 'bg-yellow-100 text-yellow-700 border-white' :
                u.role === 'teacher' ? 'bg-blue-100 text-blue-700 border-white' :
                  'bg-primary-background text-white border-primary-200'
                } ${isActive ? 'border-primary-500 ring-4 ring-primary-100' : ''}`}>
                {getInitials(u.name)}
              </div>
            )}

            {opacity > 0.6 && (
              <div className="absolute top-full mt-3 text-center w-36 pointer-events-none drop-shadow-sm">
                <p className={`text-xs font-bold truncate ${isActive ? 'text-primary-700' : 'text-gray-600'}`}>{u.name}</p>
                {isActive && <p className="text-[10px] text-primary-500 capitalize font-regular">{u.role}</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const Sidebar = ({
  className,
  isMobileMode = false,
  isMobileSidebarOpen = false,
  onMobileSidebarClose,
  ...props
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { showToast } = useToast();
  const { user, school, isLoading: isLoadingProfile } = useUserProfile();
  const permissions = usePermissions();
  const can = buildCanHelpers(permissions);
  // SuperAdmin per-section visibility (owners bypass). Only fetched/used
  // for the superadmin role; harmless otherwise.
  const saPerms = useSuperAdminPermissions();
  // Optimistic while loading so an owner never flashes an empty menu; once
  // loaded, a sub-user is filtered to only the sections they were granted.
  const saCanView = (section) =>
    !section || saPerms.loading || saPerms.isOwner || saPerms.can(section, 'view');

  // Get user role from sessionStorage
  const [userRole, setUserRole] = useState(null);
  // Associated users for mobile switcher
  const [associatedUsers, setAssociatedUsers] = useState([]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const role = sessionStorage.getItem('userRole');
      setUserRole(role);
    }

    // Fetch associated users if in mobile mode
    const fetchAssociated = async () => {
      if (!isMobileMode) return;
      try {
        const token = document.cookie.split('; ').find(row => row.startsWith('kayease_token='))?.split('=')[1] || sessionStorage.getItem('authToken');
        if (!token) return;

        const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL || ''}/api/users/associated`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success && data.associatedUsers) {
            setAssociatedUsers(data.associatedUsers);
          }
        }
      } catch (e) {
        console.error("Failed to fetch associated users for sidebar", e);
      }
    };

    fetchAssociated();

  }, [isMobileMode]);

  const handleSwitchAccount = switchToAccount;

  // Route mapping for menu items
  const getRouteForMenuItem = (key) => {
    const routeMap = {
      'dashboard': userRole === 'superadmin' ? '/superadmin-dashboard' : userRole === 'teacher' ? '/teacher-dashboard' : userRole === 'student' ? '/student-dashboard' : userRole === 'schooladmin' ? '/admin-dashboard' : '/staff-dashboard',
      'messages': '/messages',
      'helpdesk': '/helpdesk',
      'students': '/students',
      'staff': '/staff',
      'teachers': '/teachers',
      'classes': '/classes',
      'class-list': '/classes/list',
      'attendance': '/classes/attendance',
      'homework': '/classes/homework',
      'assignments': '/classes/assignments',
      'class-test': '/classes/class-test',
      'consent-form': '/classes/consent-form',
      'subject': '/subject',
      'timetable': '/timetable',
      'examinations': '/examinations',
      'grade-list': '/examinations/grades',
      'exam-list': '/examinations/list',
      'exam-schedule': '/examinations/schedule',
      'exam-results': '/examinations/results',
      'promote': '/promote',
      'transfer-certificate': '/transfer-certificates',
      'report-cards': '/report-cards',
      'activity-logs': '/activity-logs',
      'fees': '/fees',
      'fees-collection': '/fees-collection',
      'my-fees': '/my-fees',
      'my-pickup': '/my-pickup',
      'library': '/library',
      'sports': '/sports',
      'hostel': '/hostel',
      'transport': '/transport',
      'pickup': '/pickup',
      'staffs': '/staffs',
      'departments': '/departments',
      'hrm-attendance': '/attendance',
      'hrm-attendance-config': '/hrm-attendance',
      'hrm-punch-review': '/hrm-attendance/punch-review',
      'hrm-face-enrollment': '/hrm-attendance/biometric-enrollment',
      'hrm-attendance-devices': '/hrm-attendance/devices',
      'hrm-leaves': '/leaves',
      'holidays': '/holidays',
      'payroll': '/payroll',
      'payroll-run': '/payroll/run',
      'payroll-history': '/payroll/history',
      'payroll-statutory': '/payroll/statutory',
      'payroll-components': '/payroll/components',
      'payroll-bonus': '/payroll/bonus',
      'payroll-charges': '/payroll/charges',
      'my-payslips': '/my-payslips',
      'my-salary': '/my-salary',
      'notice': '/notice',
      'events': '/events',
      'admissions': '/admissions',
      'enquiries': '/enquiries',
      'attendance-report': '/attendance-report',
      'class-report': '/class-report',
      'student-report': '/student-report',
      'leave-report': '/leave-report',
      'fees-report': '/fees-report',
      'profile': '/profile',
      'permissions': '/permissions',
      'settings': '/settings',
      'website-content': '/settings/website',
      'custom-domain': '/settings/custom-domain',
      'payment-settings': '/settings/payment',
      'subscription': '/settings/subscription',
      'data-export': '/data-export',
      'manage-users': '/superadmin/manage-users',
      'schools': '/superadmin/schools',
      'demo-requests': '/superadmin/demo-requests',
      'abandoned-onboarding': '/superadmin/abandoned-onboarding',
      'my-referrals': '/superadmin/my-referrals',
      'reports': '/superadmin/reports',
      'earnings': '/superadmin/earning-analytics',
      'subscriptions': '/superadmin/subscriptions',
      'themes': '/superadmin/themes',
      'invoices': '/superadmin/invoices',
      'blogs': '/superadmin/blogs',
      'app-issues': '/superadmin/app-issues',
      'sa-permissions': '/superadmin/access-control',
      'permission-sets': '/superadmin/permission-sets',
      'activity-monitor': '/superadmin/activity-monitor',
      'server-logs': '/superadmin/server-logs',
      'superadmin-settings': '/superadmin/settings',
      'superadmin-secrets': '/superadmin/app-secrets',
      'message-templates': '/superadmin/message-templates',
      'encryption-keys': '/superadmin/encryption-keys',
      'logout': '/auth/sign-in',
    };
    return routeMap[key] || '#';
  };

  const normalizePath = (path) => {
    return path.replace(/\/$/, '').toLowerCase();
  };

  // All menus collapsed by default
  const [expandedMenus, setExpandedMenus] = useState({
    dashboard: false,
    classes: false,
    examinations: false,
    messages: false,
    manage: false,
  })

  // Collapsed state: on small screens the sidebar should be collapsed (icons-only)
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Helper to detect small screens (match Tailwind's xl breakpoint ~ 1280px)
  const isSmallScreen = () => typeof window !== 'undefined' && window.innerWidth < 1280

  useEffect(() => {
    // Only handle collapse/expand on desktop, not in mobile mode
    if (isMobileMode) return

    // Initialize collapsed state based on current viewport
    if (isSmallScreen()) setIsCollapsed(true)

    const onResize = () => {
      // If screen becomes large again, always expand
      if (!isSmallScreen()) setIsCollapsed(false)
      else setIsCollapsed(true)
    }

    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [isMobileMode])

  // Toggle a class on the body so other parts of the app (main content) can adapt
  useEffect(() => {
    try {
      const cls = 'sidebar-collapsed'
      if (typeof document !== 'undefined') {
        if (isCollapsed) document.body.classList.add(cls)
        else document.body.classList.remove(cls)
      }
    } catch (e) {
      // ignore server/client mismatch
    }
  }, [isCollapsed])

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }))
  }

  // Define all menu items with role-based access
  const allMenuItems = [
    {
      category: 'Main',
      items: [
        {
          key: 'dashboard',
          icon: '/images/img_layout_dashboard.svg',
          label: 'Dashboard',
          hasSubmenu: false,
          submenuItems: [],
          roles: ['superadmin', 'schooladmin', 'teacher', 'student', 'staff']
        },
        {
          key: 'messages',
          icon: '/images/message.svg',
          label: 'Messages',
          hasSubmenu: false,
          submenuItems: [],
          roles: ['schooladmin', 'teacher', 'student', 'staff'],
          feature: 'hasInAppMessaging'
        },
        {
          key: 'helpdesk',
          icon: '/images/help.svg',
          label: 'Help Desk',
          hasSubmenu: false,
          submenuItems: [],
          roles: ['superadmin', 'schooladmin'],
          // Grant-gated for SuperAdmin sub-users only — the filter applies
          // saSection just for role === 'superadmin', so a schooladmin still
          // reaches their own support chat as before.
          saSection: 'helpdesk',
          feature: 'hasHelpdesk'
        }
      ]
    },
    {
      category: 'Peoples',
      items: [
        {
          key: 'students',
          icon: '/images/img_school.svg',
          label: 'Students',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'students',
        },
        {
          key: 'staff',
          icon: '/images/img_user_shield.svg',
          label: 'Staff',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'staff',
        },
        {
          key: 'teachers',
          icon: '/images/img_users.svg',
          label: 'Teachers',
          hasSubmenu: false,
          roles: ['schooladmin']
        }
      ]
    },
    {
      category: 'Academic',
      items: [
        {
          key: 'classes',
          icon: '/images/img_school_bell.svg',
          label: 'Classes',
          hasSubmenu: true,
          roles: ['schooladmin', 'teacher', 'student'],
          submenuItems: [
            { label: 'Class List', key: 'class-list', roles: ['schooladmin'], section: 'classes' },
            { label: 'Attendance', key: 'attendance', roles: ['schooladmin', 'teacher', 'student'] },
            { label: 'Home Work', key: 'homework', roles: ['schooladmin', 'teacher', 'student'] },
            { label: 'Assignments', key: 'assignments', roles: ['schooladmin', 'teacher', 'student'] },
            { label: 'Class Test', key: 'class-test', roles: ['schooladmin', 'teacher', 'student'] },
            { label: 'Consent Form', key: 'consent-form', roles: ['schooladmin', 'teacher', 'student'] }
          ]
        },
        {
          key: 'subject',
          icon: '/images/img_book.svg',
          label: 'Subject',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'subjects',
        },
        {
          key: 'timetable',
          icon: '/images/img_table.svg',
          label: 'Time Table',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher', 'student']
        },
        {
          key: 'examinations',
          icon: '/images/img_hexagonal_prism_plus.svg',
          label: 'Examinations',
          hasSubmenu: true,
          roles: ['schooladmin', 'teacher', 'student'],
          section: 'examinations',
          submenuItems: [
            { label: 'Grade List', key: 'grade-list', roles: ['schooladmin'], section: 'examinations' },
            { label: 'Exam List', key: 'exam-list', roles: ['schooladmin'], section: 'examinations' },
            { label: 'Exam Schedule', key: 'exam-schedule', roles: ['schooladmin', 'teacher', 'student'] },
            { label: 'Exam Results', key: 'exam-results', roles: ['schooladmin', 'teacher', 'student'] }
          ]
        },
        {
          key: 'promote',
          icon: '/images/img_lifebuoy.svg',
          label: 'Promote',
          hasSubmenu: false,
          roles: ['schooladmin']
        },
        {
          // One entry serves both sides: schooladmin manages the issue
          // queue, students request/track/download their own TC. The
          // section tag additionally surfaces it to staff holding a
          // 'transfer-certificates' read grant.
          key: 'transfer-certificate',
          icon: '/images/img_file_text.svg',
          label: 'Transfer Certificate',
          hasSubmenu: false,
          roles: ['schooladmin', 'student'],
          section: 'transfer-certificates',
        }
      ]
    },
    {
      category: 'Management',
      items: [
        {
          key: 'fees',
          icon: '/images/img_report_money.svg',
          label: 'Fees Plans',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'fees',
        },
        {
          key: 'fees-collection',
          icon: '/images/img_report_money.svg',
          label: 'Fee Collection',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'fees',
        },
        {
          // One entry, three audiences — visibility is RELATIONSHIP-driven
          // via the 'library' section grant injected in
          // /api/permissions/me (mirrors the hostel entry), NOT a blanket
          // role:
          //   schooladmin       → always (role);
          //   librarian staff   → full manage (implicit CRUD grant);
          //   borrower (student or staff who has loans) → read-only "My
          //     Library" (implicit read grant); someone who never borrowed
          //     sees nothing.
          key: 'library',
          icon: '/images/img_notebook.svg',
          label: 'Library',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasLibrary',
          section: 'library',
        },
        {
          // RELATIONSHIP-driven visibility (mirrors library/hostel) via the
          // 'sports' section grant injected in /api/permissions/me:
          //   schooladmin       → always (role);
          //   coach staff       → full manage (implicit CRUD grant; the
          //     service still scopes them to their OWN teams);
          //   student with sports involvement → read-only "My Sports".
          key: 'sports',
          icon: '/images/img_run.svg',
          label: 'Sports',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasSports',
          section: 'sports',
        },
        {
          // One entry, three audiences — visibility is RELATIONSHIP-
          // driven via the 'hostel' section grant injected in
          // /api/permissions/me, NOT a blanket role:
          //   schooladmin  → always (role);
          //   warden staff → full manage (implicit CRUD grant);
          //   enrolled/past-resident student → read-only "My Hostel"
          //     (implicit read grant); a student who NEVER enrolled sees
          //     nothing. Same for the mobile app's Quick Menu.
          key: 'hostel',
          icon: '/images/img_building_fortress.svg',
          label: 'Hostel',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasHostel',
          section: 'hostel',
        },
        {
          // Transport visibility mirrors Hostel — driven via the
          // 'transport' section grant injected in /api/permissions/me,
          // NOT a blanket role:
          //   schooladmin        → always (role);
          //   transport manager  → full manage (implicit CRUD grant);
          //   assigned bus crew  → trip ops (implicit read+write grant);
          //   student with an active bus assignment → read-only
          //     "My Transport" (implicit read grant); a student who does
          //     not use the bus sees nothing.
          key: 'transport',
          icon: '/images/img_bus.svg',
          label: 'Transport',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasTransport',
          section: 'transport',
        },
        {
          // Student Pickup — who may collect each child + the gate register.
          // Every school gets it (owner decision D6), so there is NO
          // `feature` flag. schooladmin by role; other office staff through
          // a 'pickup' section grant. Gate staff and parents use the mobile
          // app, not this page, so students never see it.
          key: 'pickup',
          icon: '/images/img_exchange.svg',
          label: 'Student Pickup',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'pickup',
        }
      ]
    },
    {
      category: 'HRM',
      items: [
        {
          key: 'staffs',
          icon: '/images/img_users_group.svg',
          label: 'Staffs',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'staff',
        },
        {
          key: 'departments',
          icon: '/images/img_layout_distribute_horizontal.svg',
          label: 'Departments',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'departments',
        },
        {
          key: 'hrm-attendance',
          icon: '/images/img_calendar_share.svg',
          label: 'Attendance',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasHRM'
        },
        {
          key: 'hrm-attendance-config',
          icon: '/images/img_calendar_share.svg',
          label: 'Attendance Setup',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasHRM'
        },
        {
          key: 'hrm-punch-review',
          icon: '/images/img_calendar_share.svg',
          label: 'Punch Review',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasHRM'
        },
        {
          key: 'hrm-face-enrollment',
          icon: '/images/img_users_group.svg',
          label: 'Biometric Enrollment',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasHRM'
        },
        {
          key: 'hrm-attendance-devices',
          icon: '/images/img_calendar_share.svg',
          label: 'Attendance Devices',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasHRM'
        },
        {
          // Leaves is the single page every staff role lands on:
          // schooladmin sees the approval queue + their own apply
          // history, every other non-student role just sees their
          // own apply flow + history. Page-level role detection
          // shows the right tabs. We expose it to schooladmin +
          // teacher + the 'staff' wildcard (which covers all
          // non-fixed roles like accountant / librarian etc.) so
          // the entry is reachable for everyone who can apply.
          key: 'hrm-leaves',
          icon: '/images/img_calendar_stats.svg',
          label: 'Leaves',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher', 'staff'],
          feature: 'hasHRM'
        },
        {
          // Self-serve salary structure view — read-only "what's my
          // current Basic + HRA + take-home". Server scopes to the
          // user's own Staff record via `userId`. Schooladmin is a
          // system-management role with no Staff record, so this
          // entry is intentionally hidden for them — leaving it on
          // would render an empty "No staff record on file" state
          // and look like a broken feature.
          key: 'my-salary',
          icon: '/images/img_moneybag.svg',
          label: 'My Salary',
          hasSubmenu: false,
          roles: ['teacher', 'staff'],
          feature: 'hasHRM',
        },
        {
          // Self-serve payslip view. Same rationale as My Salary
          // above — schooladmin doesn't have payslips because they
          // aren't on the payroll, so the link is hidden for them.
          // Server scopes results to the user's own Staff record.
          key: 'my-payslips',
          icon: '/images/img_moneybag.svg',
          label: 'My Payslips',
          hasSubmenu: false,
          roles: ['teacher', 'staff'],
          feature: 'hasHRM',
        },
        {
          key: 'payroll',
          icon: '/images/img_moneybag.svg',
          label: 'Payroll',
          hasSubmenu: true,
          roles: ['schooladmin'],
          feature: 'hasHRM',
          section: 'payroll',
          submenuItems: [
            {
              label: 'Run Payroll',
              key: 'payroll-run',
              roles: ['schooladmin'],
              section: 'payroll',
            },
            {
              label: 'History',
              key: 'payroll-history',
              roles: ['schooladmin'],
              section: 'payroll',
            },
            {
              label: 'Statutory Settings',
              key: 'payroll-statutory',
              roles: ['schooladmin'],
              section: 'payroll',
            },
            {
              label: 'Pay Components',
              key: 'payroll-components',
              roles: ['schooladmin'],
              section: 'payroll',
            },
            {
              label: 'Statutory Bonus',
              key: 'payroll-bonus',
              roles: ['schooladmin'],
              section: 'payroll',
            },
          ],
        }
      ]
    },
    {
      category: 'Announcements',
      items: [
        {
          key: 'notice',
          icon: '/images/img_clipboard_data.svg',
          label: 'Notice Board',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher', 'student', 'staff']
        },
        {
          key: 'events',
          icon: '/images/img_calendar_question.svg',
          label: 'Events',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher', 'student', 'staff']
        },
        {
          key: 'holidays',
          icon: '/images/img_briefcase.svg',
          label: 'Holidays',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher', 'student', 'staff']
        },
        {
          key: 'my-fees',
          icon: '/images/img_report_money.svg',
          label: 'My Fees',
          hasSubmenu: false,
          roles: ['student']
        },
        {
          // Parents (on the student account) choose who may collect the
          // child from school. Self page — no section grant, no plan flag.
          key: 'my-pickup',
          icon: '/images/img_exchange.svg',
          label: 'My Pickup',
          hasSubmenu: false,
          roles: ['student']
        }
      ]
    },
    {
      category: 'Website Forms',
      items: [
        {
          key: 'admissions',
          icon: '/images/img_clipboard_data.svg',
          label: 'Application Forms',
          hasSubmenu: false,
          roles: ['schooladmin']
        },
        {
          key: 'enquiries',
          icon: '/images/message.svg',
          label: 'Enquiries',
          hasSubmenu: false,
          roles: ['schooladmin']
        }
      ]
    },
    {
      category: 'Reports',
      items: [
        {
          key: 'attendance-report',
          icon: '/images/img_calendar_due.svg',
          label: 'Attendance Report',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher', 'student'],
          feature: 'hasReports',
          section: 'reports',
        },
        {
          key: 'class-report',
          icon: '/images/img_graph.svg',
          label: 'Class Report',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher'],
          feature: 'hasReports',
          section: 'reports',
        },
        {
          key: 'student-report',
          icon: '/images/img_chart_infographic.svg',
          label: 'Student Report',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher'],
          feature: 'hasReports',
          section: 'reports',
        },
        {
          key: 'leave-report',
          icon: '/images/img_line.svg',
          label: 'Leave Report',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher'],
          features: ['hasHRM', 'hasReports'],
          section: 'reports',
        },
        {
          key: 'fees-report',
          icon: '/images/img_mask.svg',
          label: 'Fees Report',
          hasSubmenu: false,
          roles: ['schooladmin'],
          feature: 'hasReports',
          section: 'reports',
        },
        {
          // One entry serves both sides: admins generate/publish
          // marksheets, students download their own published card.
          // Own section key ('report-cards') so the grant is separable
          // from the read-only 'reports' pages.
          key: 'report-cards',
          icon: '/images/img_file_text.svg',
          label: 'Report Cards',
          hasSubmenu: false,
          roles: ['schooladmin', 'teacher', 'student'],
          feature: 'hasReports',
          section: 'report-cards',
        }
      ]
    },
    {
      // ── SuperAdmin categories ─────────────────────────────────────────
      // Superadmin-only groups: the per-role filter drops empty categories,
      // so school roles never see these headings (and superadmin no longer
      // sees one giant "Manage" pile).
      category: 'Schools',
      items: [
        {
          key: 'schools',
          icon: '/images/img_school.svg',
          label: 'Schools',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'schools',
        },
        {
          key: 'demo-requests',
          icon: '/images/message.svg',
          label: 'Demo Requests',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'demo-requests',
        },
        {
          key: 'abandoned-onboarding',
          icon: '/images/img_clipboard_data.svg',
          label: 'Abandoned Onboarding',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'abandoned-onboarding',
        },
        {
          key: 'my-referrals',
          icon: '/images/img_school.svg',
          label: 'My Referrals',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'my-referrals',
        },
      ]
    },
    {
      category: 'Revenue',
      items: [
        {
          key: 'subscriptions',
          icon: '/images/img_report_money.svg',
          label: 'Subscription Plans',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'subscriptions',
        },
        {
          key: 'invoices',
          icon: '/images/img_clipboard_data.svg',
          label: 'Master Invoices',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'invoices',
        },
        {
          key: 'earnings',
          icon: '/images/img_report_money.svg',
          label: 'Earnings Analytics',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'earnings',
        },
        {
          key: 'reports',
          icon: '/images/img_graph.svg',
          label: 'Reports',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'reports',
        },
      ]
    },
    {
      category: 'Platform',
      items: [
        {
          key: 'themes',
          icon: '/images/img_layout_distribute_horizontal.svg',
          label: 'Themes',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'themes',
        },
        {
          key: 'blogs',
          icon: FileText,
          label: 'Blogs',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'blogs',
        },
        {
          key: 'app-issues',
          icon: Bug,
          label: 'App Issues',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'app-issues',
        },
        {
          key: 'message-templates',
          icon: '/images/settings-up.svg',
          label: 'Message Templates',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'message-templates',
        },
      ]
    },
    {
      category: 'Team & Security',
      items: [
        {
          key: 'manage-users',
          icon: '/images/img_users.svg',
          label: 'Manage Users',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'superadmin-users',
        },
        {
          key: 'sa-permissions',
          icon: '/images/img_users_group.svg',
          label: 'Access Control',
          hasSubmenu: false,
          roles: ['superadmin'],
          ownerOnly: true,
        },
        {
          key: 'permission-sets',
          icon: '/images/img_users_group.svg',
          label: 'Permission Sets',
          hasSubmenu: false,
          roles: ['superadmin'],
          ownerOnly: true,
        },
        {
          key: 'activity-monitor',
          icon: '/images/img_file_text.svg',
          label: 'Activity Monitor',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'activity-logs',
        },
        {
          // Raw application logs, read-only — so a developer can debug
          // production without SSH. Its own grant: pino redacts credentials
          // but URLs, IPs and stack traces still pass through here.
          key: 'server-logs',
          icon: '/images/img_file_text.svg',
          label: 'Server Logs',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'server-logs',
        },
        {
          key: 'encryption-keys',
          icon: '/images/settings-up.svg',
          label: 'Encryption Keys',
          hasSubmenu: false,
          roles: ['superadmin'],
          // Status page is view-delegable via the 'encryption-keys' grant
          // (in SA_SECTION_CATALOGUE, so it appears in Permission Sets).
          // Rotation itself stays owner-only at the backend (requireOwner)
          // and the page disables those buttons for non-owners.
          saSection: 'encryption-keys',
        },
      ]
    },
    {
      // School-side management (superadmin items moved to their own
      // categories above — for schooladmin this still renders as "Manage").
      category: 'Manage',
      items: [
        {
          // Operation audit trail — schooladmin by default, delegable
          // via the 'activity-logs' section grant.
          key: 'activity-logs',
          icon: '/images/img_file_text.svg',
          label: 'Activity Logs',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'activity-logs',
        },
      ]
    },
    {
      category: 'Profile & Settings',
      items: [
        {
          // Permissions matrix — schooladmin grants individual staff
          // members additional access (e.g. let HR approve leaves,
          // let a librarian write/update Holidays). Lives in the
          // Settings category since it's a management surface; the
          // page itself enforces schooladmin via AuthGuard + server.
          key: 'permissions',
          icon: '/images/img_users_group.svg',
          label: 'Permissions',
          hasSubmenu: false,
          roles: ['schooladmin'],
        },
        {
          key: 'settings',
          icon: '/images/settings-up.svg',
          label: 'Settings',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'settings',
        },
        {
          // Data Export — the school requests a copy of their own records
          // (spreadsheets + photos, built in the background and emailed
          // when ready). Not subscription-gated: every school owns its
          // data. The exportable sections inside ARE plan-filtered.
          key: 'data-export',
          icon: '/images/img_file_text.svg',
          label: 'Data Export',
          hasSubmenu: false,
          roles: ['schooladmin'],
          section: 'data-export',
        },
        {
          key: 'website-content',
          icon: '/images/img_layout_distribute_horizontal.svg',
          label: 'Website Content',
          hasSubmenu: false,
          roles: ['schooladmin']
        },
        {
          key: 'custom-domain',
          icon: '/images/img_layout_distribute_horizontal.svg',
          label: 'Custom Domain',
          hasSubmenu: false,
          roles: ['schooladmin']
        },
        {
          key: 'payment-settings',
          icon: '/images/img_report_money.svg',
          label: 'Payment Integration',
          hasSubmenu: false,
          roles: ['schooladmin']
        },
        {
          key: 'subscription',
          icon: '/images/img_report_money.svg',
          label: 'Subscription',
          hasSubmenu: false,
          roles: ['schooladmin']
        },
        {
          key: 'superadmin-settings',
          icon: '/images/settings-up.svg',
          label: 'Business Profile',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'business-profile',
        },
        {
          key: 'superadmin-secrets',
          icon: '/images/settings-up.svg',
          label: 'API Keys & Secrets',
          hasSubmenu: false,
          roles: ['superadmin'],
          saSection: 'app-secrets',
        },
        {
          key: 'logout',
          icon: '/images/logout.svg',
          label: 'LogOut',
          hasSubmenu: false,
          roles: ['superadmin', 'schooladmin', 'teacher', 'student', 'staff']
        }
      ]
    }
  ];

  // Roles in SchoolUser are partly fixed (superadmin / schooladmin /
  // teacher / student) and partly dynamic — non-teaching staff have a
  // designation-derived role string like 'Accountant' or 'Librarian'.
  // For sidebar matching we treat every dynamic-staff role as a
  // synonym of 'staff', so an item only needs to list 'staff' once to
  // be visible to every dynamic staff member.
  const FIXED_ROLES = ['superadmin', 'schooladmin', 'teacher', 'student'];
  const roleMatches = (itemRoles, role) => {
    if (!itemRoles) return true;
    if (itemRoles.includes(role)) return true;
    if (!FIXED_ROLES.includes(role) && itemRoles.includes('staff')) return true;
    return false;
  };

  // Filter menu items based on user role + permission overlay.
  //
  // An item is visible when ALL of the following hold:
  //   (a) Role gate: role matches `item.roles` OR the user has been
  //       granted `read` on `item.section` via the UserPermission
  //       overlay. The section grant is additive — it can WIDEN
  //       access beyond role defaults, never narrow it.
  //   (b) Subscription gate: every flag in `item.feature(s)` is
  //       enabled on the school's plan (superadmins skip this).
  //   (c) Submenu non-empty (for `hasSubmenu` items): at least one
  //       sub-item passes its own role + feature + section checks.
  const filterMenuItemsByRole = (items, role) => {
    if (!role) return [];

    // The section.read override. Schooladmin bypass is folded into
    // `can.read` (returns true unconditionally for them).
    //
    // The section overlay is a SCHOOL-side concept: it widens a staff
    // member's access *within their own school*. Superadmin is never on
    // a school's UserPermission overlay, so it must NOT pick up
    // schooladmin-only items just because they carry a `section:` tag —
    // otherwise the entire School Admin menu (Students, Staff, Fees,
    // Payroll, Reports, Settings…) leaks into the SuperAdmin sidebar,
    // which is meant for managing schools + our SaaS service only.
    // Superadmin visibility is decided entirely by the role gate above.
    const sectionReadOk = (section) => {
      if (!section) return false;
      if (role === 'superadmin') return false;
      return can.read(section);
    };

    return items.filter(item => {
      // 1. Role check OR section-read grant override.
      const hasRoleAccess =
        roleMatches(item.roles, role) || sectionReadOk(item.section);
      if (!hasRoleAccess) return false;

      // 1b. SuperAdmin gating: owner-only items show only for owners; a
      // sub-user sees a saSection item only if granted it (owners bypass
      // via saCanView). Items with neither stay visible to any superadmin.
      if (role === 'superadmin') {
        if (item.ownerOnly && !(saPerms.loading || saPerms.isOwner)) return false;
        if (item.saSection && !saCanView(item.saSection)) return false;
      }

      // 2. Feature Check (Subscription Plan)
      // Superadmins see everything. Others are restricted by plan.
      // `feature` (string) or `features` (string[]) — all listed flags must be true.
      if (role !== 'superadmin' && (item.feature || item.features)) {
        const flags = item.features || [item.feature];
        const hasAllFeatures = flags.every(f => school?.subscription?.[f]);
        if (!hasAllFeatures) return false;
      }

      // 3. Submenu Check
      if (item.hasSubmenu && item.submenuItems) {
        const visibleSubItems = item.submenuItems.filter(subItem => {
          // Sub-role check + section override (same rule as parent)
          const subRoleOk =
            roleMatches(subItem.roles, role) ||
            sectionReadOk(subItem.section);
          if (!subRoleOk) return false;

          // Sub-feature check
          if (role !== 'superadmin' && (subItem.feature || subItem.features)) {
            const subFlags = subItem.features || [subItem.feature];
            const subFeatureOk = subFlags.every(f => school?.subscription?.[f]);
            if (!subFeatureOk) return false;
          }

          return true;
        });

        // Mutate local copy for rendering
        item.submenuItems = visibleSubItems;

        // Only show parent if it has visible submenu items
        return item.submenuItems.length > 0;
      }

      return true;
    });
  };

  const menuItems = allMenuItems
    .map(section => ({
      ...section,
      items: filterMenuItemsByRole(section.items, userRole)
    }))
    .filter(section => section.items.length > 0); // Remove empty sections

  // In mobile mode, always show expanded (not collapsed)
  const isMobileExpanded = isMobileMode ? true : !isCollapsed

  return (
    <aside
      className={twMerge(
        // Mobile mode: full width overlay, fixed positioning
        isMobileMode
          ? 'fixed inset-0 w-full h-full bg-sidebar-background z-50'
          : // Desktop mode: normal sidebar layout
          'h-screen bg-sidebar-background border-r border-sidebar-border sticky top-0 z-30 flex-shrink-0',
        // Desktop: when collapsed use a narrow fixed width, otherwise use the original percent/min widths
        // Expanded: the 180px floor was too tight on ~1280-1440px laptops
        // (MacBook 13") — long labels like "Biometric Enrollment" wrapped to
        // two lines. 220px fits the longest label on one line; max-w still
        // governs on wide screens so those are unchanged.
        !isMobileMode && (isCollapsed ? 'w-[72px] min-w-[72px] max-w-[72px]' : 'w-full max-w-[14%] min-w-[220px]'),
        // Hide sidebar on mobile (desktop view), show when mobile mode is active
        !isMobileMode ? 'hidden xl:block' : 'block',
        className
      )}
      {...props}
    >
      <div className={twMerge(
        'flex flex-col h-full',
        isMobileMode ? 'p-4' : (isCollapsed ? 'p-2' : 'p-4')
      )}>
        {/* Fixed Header Section - Optimized for landscape */}
        <div className="flex-shrink-0">
          {/* Mobile Mode: Profile Section at Top */}
          {isMobileMode && (
            <div className="flex flex-col items-center gap-2 landscape:gap-1 mb-4 landscape:mb-2 pb-4 landscape:pb-2 border-b border-border-primary">
              <button
                onClick={onMobileSidebarClose}
                className="self-end p-2 hover:bg-background-hover rounded-md transition-colors duration-150"
                aria-label="Close menu"
              >
                <svg
                  className="w-5 h-5 text-text-primary"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              {/* Profile Image (Round) - Compact in landscape */}
              <div className="flex flex-col items-center gap-1.5 landscape:gap-0.5">
                {user?.photoUrl ? (
                  <SafeImage
                    src={user.photoUrl}
                    alt={user.name || 'Profile'}
                    variant="avatar"
                    name={user?.name}
                    className="w-20 h-20 landscape:w-12 landscape:h-12 rounded-full border-2 border-border-primary object-cover"
                  />
                ) : userRole === 'schooladmin' ? (
                  isLoadingProfile ? (
                    <div className="w-20 h-20 landscape:w-12 landscape:h-12 rounded-full border-2 border-border-primary bg-gray-200 animate-pulse" />
                  ) : (
                    <SafeImage
                      src={school?.logoUrl}
                      alt={school?.schoolName || 'School Logo'}
                      variant="logo"
                      fallbackSrc="/logo.png"
                      className="w-20 h-20 landscape:w-12 landscape:h-12 rounded-full border-2 border-border-primary object-contain bg-white"
                    />
                  )
                ) : (
                  <div className="w-20 h-20 landscape:w-12 landscape:h-12 rounded-full border-2 border-border-primary bg-primary-background text-white flex items-center justify-center text-xl landscape:text-sm font-semibold">
                    {getInitials(user?.name)}
                  </div>
                )}
                {/* Profile Name - Compact in landscape */}
                <div className="text-center">
                  <p className="text-base landscape:text-sm font-semibold text-text-primary">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-sm landscape:text-xs text-text-secondary capitalize">
                    {userRole === 'schooladmin' ? 'School Admin' : userRole === 'teacher' ? 'Teacher' : userRole === 'student' ? 'Student' : 'User'}
                  </p>
                </div>
              </div>

              {/* Associated Accounts - Compact in landscape */}
              {associatedUsers.length > 1 && (
                <div className="w-full mt-3 landscape:mt-1.5">
                  <p className="text-xs landscape:text-[10px] text-center text-text-secondary mb-1.5 landscape:mb-1 uppercase tracking-wide font-medium">Switch To</p>
                  <div className="flex items-center justify-center gap-2.5 landscape:gap-1.5 flex-wrap">
                    {associatedUsers
                      .filter(u => u.id !== user?.id && u.id !== user?._id) // Show others only
                      .map((u) => (
                        <button
                          key={u.id}
                          onClick={() => handleSwitchAccount(u)}
                          className="group relative flex flex-col items-center transition-transform hover:scale-105"
                          title={u.school?.schoolName ? `Switch to ${u.name} (${u.school.schoolName})` : `Switch to ${u.name}`}
                        >
                          <div className={`w-10 h-10 landscape:w-8 landscape:h-8 rounded-lg overflow-hidden border-2 border-transparent group-hover:border-primary-300 shadow-sm ${!u.photoUrl ? (u.role === 'student' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700') : ''} flex items-center justify-center`}>
                            {u.photoUrl ? (
                              <SafeImage src={u.photoUrl} alt={u.name} variant="avatar" name={u.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="font-bold text-xs landscape:text-[10px]">{getInitials(u.name)}</span>
                            )}
                          </div>
                          {/* Optional: Tiny name below? Keeping it clean as 'small rectangles' requested */}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Desktop Header */}
          {!isMobileMode && (
            <div className={isCollapsed ? 'flex items-center justify-center mb-6' : 'flex items-center justify-between mb-6'}>
              {/* When expanded show logo on left */}
              {!isCollapsed && (
                <button
                  type="button"
                  className="flex items-center gap-2"
                  onClick={() => {
                    const dashboardRoute = getRouteForMenuItem('dashboard');
                    if (dashboardRoute && dashboardRoute !== '#') {
                      router.push(dashboardRoute);
                    }
                  }}
                >
                  {isLoadingProfile ? (
                    <div className="w-[114px] h-[32px] bg-gray-200 animate-pulse rounded" />
                  ) : (
                    <SafeImage
                      src={school?.logoUrl}
                      alt={school?.schoolName || 'Kaympus'}
                      variant="logo"
                      fallbackSrc="/logo.png"
                      className="w-[114px] h-[32px] object-contain"
                    />
                  )}
                </button>
              )}

              {/* Toggle collapse (acts like hamburger on small screens) */}
              <div>
                <button
                  className="p-1"
                  onClick={() => setIsCollapsed(prev => !prev)}
                  aria-label={isCollapsed ? 'Open sidebar' : 'Collapse sidebar'}
                >
                  <Menu size={18} strokeWidth={2} />
                </button>
              </div>
            </div>
          )}

          {/* School Info Card - hide when collapsed (icons-only view) or in mobile mode */}
          {!isMobileMode && !isCollapsed && userRole !== 'superadmin' && (
            <div className="flex items-center gap-2 p-3 mb-6 bg-secondary-background border border-border-primary rounded-md shadow-sm">
              <div className="flex-1">
                <p className="text-sm font-normal text-text-primary">
                  {isLoadingProfile ? 'Loading...' : (school?.shortName || school?.schoolName || 'School')}
                </p>
              </div>
              <div className="flex flex-col">
                <img
                  src="/images/img_chevron_up.svg"
                  alt="Up"
                  className="w-2.5 h-2.5"
                />
                <img
                  src="/images/img_chevron_up.svg"
                  alt="Down"
                  className="w-2.5 h-2.5 -mt-1 rotate-180"
                />
              </div>
            </div>
          )}

        </div>
        {/* Scrollable Navigation Menu */}
        <div className="flex-1 overflow-y-auto flex flex-col">
          <nav className={twMerge(
            'flex-1',
            isMobileMode || !isCollapsed ? 'space-y-6' : 'space-y-4 flex flex-col items-center'
          )}>
            {menuItems.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                {/* hide section titles in collapsed/icons-only mode, but show in mobile mode */}
                {(isMobileMode || !isCollapsed) && (
                  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                    {section.category}
                  </h3>
                )}
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.key}>
                      {item.hasSubmenu ? (
                        <div>
                          <button
                            data-testid={`sidebar-menu-${item.key}-toggle`}
                            onClick={() => {
                              // If currently collapsed (icons-only) -> expand the sidebar and open this menu
                              if (isCollapsed && !isMobileMode) {
                                setIsCollapsed(false)
                                setExpandedMenus(prev => ({ ...prev, [item.key]: true }))
                              } else {
                                toggleMenu(item.key)
                              }
                            }}
                            className={twMerge(
                              // center icons and remove large horizontal padding when collapsed (desktop only)
                              isMobileMode || !isCollapsed
                                ? 'flex items-center justify-between w-full p-2 text-sm text-text-primary hover:bg-background-hover rounded-md transition-colors duration-150'
                                : 'flex items-center justify-center w-10 h-10 p-0 text-sm text-text-primary hover:bg-background-hover rounded-md transition-colors duration-150'
                            )}
                          >
                            <div className={isMobileMode || !isCollapsed ? 'flex items-center gap-3 min-w-0' : 'flex items-center justify-center'}>
                              {typeof item.icon === 'string' ? (
                                <img
                                  src={item.icon}
                                  alt={item.label}
                                  className="w-3.5 h-3.5 flex-shrink-0"
                                />
                              ) : (
                                <item.icon
                                  aria-label={item.label}
                                  className="w-3.5 h-3.5 flex-shrink-0"
                                />
                              )}
                              {(isMobileMode || !isCollapsed) && <span className="truncate min-w-0">{item.label}</span>}
                            </div>
                            {(isMobileMode || !isCollapsed) && (
                              <img
                                src="/images/img_arrow_dropdown.svg"
                                alt="Dropdown"
                                className={twMerge(
                                  'w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200',
                                  expandedMenus[item.key] ? 'rotate-180' : ''
                                )}
                              />
                            )}
                          </button>
                          {expandedMenus[item.key] && item.submenuItems && (
                            // only show submenu when sidebar is expanded (or in mobile mode)
                            (isMobileMode || !isCollapsed) && (
                              <ul className="ml-6 mt-1 space-y-1">
                                {item.submenuItems.map((subItem, subIndex) => {
                                  const subRoute = getRouteForMenuItem(subItem.key);
                                  return (
                                    <li key={subIndex}>
                                      <button
                                        data-testid={`sidebar-submenu-${subItem.key}-btn`}
                                        className={twMerge(
                                          "flex items-center gap-2 w-full p-2 text-sm rounded-md transition-colors duration-150",
                                          pathname === subRoute
                                            ? 'bg-primary-background text-white shadow-md hover:bg-primary-light'
                                            : 'text-text-secondary hover:text-text-primary hover:bg-background-hover'
                                        )}
                                        onClick={() => {
                                          // Navigate to submenu route
                                          if (subRoute && subRoute !== '#') {
                                            const normalizedPath = normalizePath(pathname);
                                            const normalizedSub = normalizePath(subRoute);
                                            if (normalizedPath === normalizedSub) {
                                              // Add reset param to trigger state reset in page
                                              router.push(`${subRoute}?reset=${Date.now()}`);
                                            } else {
                                              router.push(subRoute);
                                            }
                                          }
                                          // After selecting a submenu on mobile, close sidebar
                                          if (isMobileMode && onMobileSidebarClose) {
                                            onMobileSidebarClose()
                                          }
                                          // After selecting a submenu on small screens, collapse again
                                          if (!isMobileMode && isSmallScreen()) setIsCollapsed(true)
                                        }}
                                      >
                                        <div className={twMerge(
                                          "w-2 h-1 rounded-full flex-shrink-0",
                                          pathname === subRoute ? "bg-white" : "bg-text-secondary"
                                        )}></div>
                                        <span className="truncate min-w-0">{subItem.label}</span>
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )
                          )}
                        </div>
                      ) : (
                        <button
                          data-testid={`sidebar-nav-${item.key}-btn`}
                          title={item.label}
                          className={twMerge(
                            isMobileMode || !isCollapsed
                              ? 'flex items-center gap-3 w-full p-2 text-sm rounded-md transition-colors duration-150'
                              : 'flex items-center justify-center w-10 h-10 p-0 text-sm rounded-md transition-colors duration-150',
                            // Highlight active route
                            pathname === getRouteForMenuItem(item.key)
                              ? 'bg-primary-background text-white shadow-md hover:bg-primary-light'
                              : 'text-text-primary hover:bg-background-hover'
                          )}
                          onClick={() => {
                            // Handle logout specially
                            if (item.key === 'logout') {
                              logout(router, showToast);
                              // After logout on mobile, close sidebar
                              if (isMobileMode && onMobileSidebarClose) {
                                onMobileSidebarClose()
                              }
                              return;
                            }

                            // Navigate to the route for this menu item
                            const route = getRouteForMenuItem(item.key);
                            if (route && route !== '#') {
                              const normalizedPath = normalizePath(pathname);
                              const normalizedRoute = normalizePath(route);
                              if (normalizedPath === normalizedRoute) {
                                // Add reset param to trigger state reset in page
                                router.push(`${route}?reset=${Date.now()}`);
                              } else {
                                router.push(route);
                              }
                            }
                            // After selecting an item on mobile, close sidebar
                            if (isMobileMode && onMobileSidebarClose) {
                              onMobileSidebarClose()
                            }
                            // After selecting an item on small screens, collapse again
                            if (!isMobileMode && isSmallScreen()) setIsCollapsed(true)
                          }}
                        >
                          {typeof item.icon === 'string' ? (
                            <img
                              src={item.icon}
                              alt={item.label}
                              className={twMerge(
                                "w-3.5 h-3.5 flex-shrink-0",
                                pathname === getRouteForMenuItem(item.key) ? "brightness-0 invert" : ""
                              )}
                            />
                          ) : (
                            <item.icon
                              aria-label={item.label}
                              className={twMerge(
                                "w-3.5 h-3.5 flex-shrink-0",
                                pathname === getRouteForMenuItem(item.key) ? "text-white" : ""
                              )}
                            />
                          )}
                          {(isMobileMode || !isCollapsed) && <span className="truncate min-w-0">{item.label}</span>}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
      </div>
    </aside>
  )
}
export default Sidebar
